import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, KeyRound } from 'lucide-react';
import { apiClient } from '../services/api';
import { useAuth } from '../hooks/useAuth';

interface MagicCodeFlowProps {
  onBack: () => void;
  mode?: 'signin' | 'signup';
}

export const MagicCodeFlow: React.FC<MagicCodeFlowProps> = ({ onBack, mode = 'signup' }) => {
  const navigate = useNavigate();
  const { loginWithMagicCode } = useAuth();

  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendConfirm, setResendConfirm] = useState(false);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.requestMagicCode({
        email: email.trim(),
        first_name: username.trim(),
        last_name: '',
      });
      setStep('verify');
    } catch {
      setError('Could not send the code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await loginWithMagicCode(email.trim(), code.trim());
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Invalid or expired code. Please try again or request a new one.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResendConfirm(false);
    try {
      await apiClient.requestMagicCode({
        email: email.trim(),
        first_name: username.trim(),
        last_name: '',
      });
      setCode('');
      setResendConfirm(true);
      setTimeout(() => setResendConfirm(false), 3000);
    } catch {
      setError('Could not resend the code. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={step === 'verify' ? () => setStep('request') : onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {step === 'request' && (
        <form onSubmit={handleRequestCode} className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Sign in with a code</h3>
            <p className="text-sm text-gray-500 mt-1">We'll email you a 6-digit code — no password needed.</p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {mode === 'signup' && (
            <div>
              <label htmlFor="mc-username" className="block text-sm font-medium text-gray-700">
                Username
              </label>
              <input
                id="mc-username"
                type="text"
                required
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Your username"
              />
            </div>
          )}

          <div>
            <label htmlFor="mc-email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="mc-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="you@example.com"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            {isLoading ? 'Sending…' : 'Send code'}
          </button>
        </form>
      )}

      {step === 'verify' && (
        <form onSubmit={handleVerify} className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Enter your code</h3>
            <p className="text-sm text-gray-500 mt-1">
              We sent a 6-digit code to <span className="font-medium text-gray-700">{email}</span>. It expires in 10 minutes.
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {resendConfirm && (
            <div className="rounded-md bg-green-50 p-3">
              <p className="text-sm text-green-700">Code resent — check your inbox.</p>
            </div>
          )}

          <div>
            <label htmlFor="mc-code" className="block text-sm font-medium text-gray-700">
              6-digit code
            </label>
            <input
              id="mc-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              autoFocus
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm tracking-widest font-mono focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="483921"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <KeyRound className="h-4 w-4" />
            )}
            {isLoading ? 'Verifying…' : 'Verify'}
          </button>

          <p className="text-center text-sm text-gray-500">
            Didn't receive it?{' '}
            <button
              type="button"
              onClick={handleResend}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Resend code
            </button>
          </p>
        </form>
      )}
    </div>
  );
};
